/** Personal layer: the signed-in person's own Microsoft 365 calendar and mail (live, read-only). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, ExternalLink, Mail, Unplug, Video } from "lucide-react";
import { OutlookAddinCard } from "../components/OutlookAddin";
import { useEffect, useState } from "react";
import { api, del } from "../lib/api";
import { Card, ErrorNote } from "../components/ui";

interface Conn { available: boolean; connected: boolean; accountEmail?: string; accountName?: string | null; error?: string | null }
interface Ev { id: string; subject: string; start: string; end: string; isAllDay: boolean; location: string | null; webLink: string; onlineMeetingUrl: string | null; organizer: string | null }
interface Msg { id: string; subject: string; from: string | null; receivedAt: string; isRead: boolean; importance: string; preview: string; webLink: string }

export function useM365() {
  return useQuery({ queryKey: ["personal", "connections"], queryFn: () => api<{ microsoft: Conn }>("/api/personal/connections"), staleTime: 60_000 });
}

const time = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
const dayLabel = (iso: string) => {
  const d = new Date(iso), t = new Date();
  const diff = Math.round((new Date(d.toDateString()).getTime() - new Date(t.toDateString()).getTime()) / 86_400_000);
  return diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  return m < 60 ? `${Math.max(m, 1)}m` : m < 1440 ? `${Math.round(m / 60)}h` : new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/** Shows the result of the Microsoft consent redirect (?m365=connected|error) once, then cleans the URL. */
function useConnectResult() {
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get("m365");
    if (!r) return;
    setNote(r === "connected" ? { ok: true, text: "Microsoft 365 connected." }
      : r === "approved" ? { ok: true, text: "ONE is approved for the whole organisation. Everyone can now connect Microsoft 365 and press Allow themselves — no admin needed." }
      : { ok: false, text: q.get("reason") ?? "Microsoft 365 could not be connected." });
    q.delete("m365");
    q.delete("reason");
    window.history.replaceState(null, "", `${window.location.pathname}${q.size ? `?${q}` : ""}`);
  }, []);
  return note;
}

function Agenda() {
  const cal = useQuery({ queryKey: ["personal", "calendar"], queryFn: () => api<{ items: Ev[] | null }>("/api/personal/calendar?days=3"), refetchInterval: 5 * 60_000 });
  if (cal.error) return <ErrorNote error={cal.error} />;
  const items = cal.data?.items ?? [];
  if (cal.isLoading) return <p className="muted text-sm">Loading calendar…</p>;
  if (!items.length) return <p className="muted text-sm">No meetings in the next three days.</p>;
  let last = "";
  return (
    <ul className="space-y-1.5">
      {items.slice(0, 8).map((e) => {
        const label = dayLabel(e.start);
        const head = label !== last ? ((last = label), label) : null;
        return (
          <li key={e.id}>
            {head && <div className="muted mb-1 mt-2 text-[11px] font-semibold uppercase tracking-wide first:mt-0">{head}</div>}
            <div className="flex items-start gap-2 text-sm">
              <span className="muted w-24 shrink-0 tabular-nums">{e.isAllDay ? "All day" : `${time(e.start)}–${time(e.end)}`}</span>
              <a href={e.webLink} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline" title={e.location ?? undefined}>{e.subject}</a>
              {e.onlineMeetingUrl && <a href={e.onlineMeetingUrl} target="_blank" rel="noreferrer" className="text-brand-600" title="Join online"><Video size={15} /></a>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function MailList() {
  const m = useQuery({ queryKey: ["personal", "mail"], queryFn: () => api<{ unread: number; items: Msg[] | null }>("/api/personal/mail?top=6"), refetchInterval: 5 * 60_000 });
  if (m.error) return <ErrorNote error={m.error} />;
  if (m.isLoading) return <p className="muted text-sm">Loading mail…</p>;
  const items = m.data?.items ?? [];
  return (
    <div>
      <div className="muted mb-1.5 text-xs">{m.data?.unread ?? 0} unread in Inbox</div>
      {items.length ? (
        <ul className="space-y-1.5">
          {items.map((x) => (
            <li key={x.id}>
              <a href={x.webLink} target="_blank" rel="noreferrer" className="flex items-baseline gap-2 text-sm hover:underline">
                {!x.isRead && <span className="h-2 w-2 shrink-0 translate-y-[-1px] rounded-full bg-brand-600" aria-label="unread" />}
                <span className={`min-w-0 flex-1 truncate ${x.isRead ? "" : "font-semibold"}`}>{x.from ? `${x.from} — ` : ""}{x.subject}</span>
                <span className="muted shrink-0 text-xs">{ago(x.receivedAt)}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : <p className="muted text-sm">Inbox is empty.</p>}
    </div>
  );
}

/** Settings: the Microsoft 365 connection, then the Outlook add-in. */
export function M365Card() {
  return (
    <>
      <M365Connection />
      <OutlookAddinCard />
    </>
  );
}

function M365Connection() {
  const conn = useM365();
  const qc = useQueryClient();
  const note = useConnectResult();
  const off = useMutation({ mutationFn: () => del("/api/personal/microsoft"), onSuccess: () => qc.invalidateQueries({ queryKey: ["personal"] }) });
  const c = conn.data?.microsoft;
  if (!c?.available) return null;
  const banner = note && <div className={`mb-3 rounded-lg px-3 py-2 text-sm ${note.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>{note.text}</div>;
  if (!c.connected) {
    return (
      <Card title="My Microsoft 365">
        {banner}
        <p className="muted mb-3 text-sm">See your Outlook calendar and mail here, next to your work. Only you can see them, and ONE never changes anything in Microsoft 365.</p>
        <a className="btn btn-primary" href="/api/personal/microsoft/connect">Connect Microsoft 365</a>
      </Card>
    );
  }
  return (
    <Card
      title="My Microsoft 365"
      actions={<button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => off.mutate()} disabled={off.isPending} title={`Disconnect ${c.accountEmail}`}><Unplug size={13} /> Disconnect</button>}
    >
      <div className="space-y-4">
        {banner}
        {c.error && <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{c.error} <a className="font-medium underline" href="/api/personal/microsoft/connect">Reconnect</a></div>}
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold"><Calendar size={14} className="text-brand-600" /> Calendar <a className="muted ml-auto" href="https://outlook.office.com/calendar" target="_blank" rel="noreferrer"><ExternalLink size={12} /></a></div>
          <Agenda />
        </div>
        <div className="line border-t pt-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold"><Mail size={14} className="text-brand-600" /> Mail <a className="muted ml-auto" href="https://outlook.office.com/mail" target="_blank" rel="noreferrer"><ExternalLink size={12} /></a></div>
          <MailList />
        </div>
        <p className="muted text-[11px]">Connected as {c.accountEmail}. Only you can see this.</p>
      </div>
    </Card>
  );
}


/** Today's right column: your Outlook meetings only (mail is under "Email that needs you"; connect/disconnect is in Settings). */
export function AgendaCard() {
  const c = useM365().data?.microsoft;
  if (!c?.available) return null;
  if (!c.connected) {
    return (
      <p className="muted rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm">
        <Calendar size={14} className="mr-1.5 inline text-brand-600" />See your meetings here: <a className="font-medium text-brand-700 hover:underline" href="/api/personal/microsoft/connect">Connect Microsoft 365</a>
      </p>
    );
  }
  return (
    <Card title="Agenda" icon={<Calendar size={16} />} actions={<a className="text-xs font-semibold text-brand-600 hover:underline" href="https://outlook.office.com/calendar" target="_blank" rel="noreferrer">Open Outlook</a>}>
      {c.error && <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{c.error} <a className="font-medium underline" href="/api/personal/microsoft/connect">Reconnect</a></div>}
      <Agenda />
    </Card>
  );
}
